import React, { useState } from 'react';
import { Stack, Chip, Button, CircularProgress, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ViewAllFiltersModal from './ViewAllFiltersModal';
import EditBondCharacteristic from '../BondLadderComponents/EditBondCharacteristic';
import { useBondCharacteristicStore } from '../../Store/bondCharacteristicStore';
import { getBondCharacteristicById } from '../../http/api';
import { useNavigate } from 'react-router-dom';
import { handleViewInventory } from '../../Utils/viewInventoryHandler';
import { processBondData } from '../OrderComponents/utils';
import { useActualOrderStore } from '../../Store/actualOrderStore';
import { tokens } from '../../Common/designTokens'; // added

const FilterChips = ({ 
  input, 
  onDeleteChip, 
  onClearFilters,
  onEditFilter, // New prop to handle editing
  onEditSavedFilter, // New prop specifically for saved filter editing
  selectedFilter, // New prop to know which saved filter is active
  savedFilters, // New prop to get saved filter data
  maxVisibleChips = 2,
  readOnly = false, // New prop to control whether chips can be deleted
  compact = false,   // New prop for smaller chips in columns
  ladderName = null, // <-- new prop for ladderName
  labelFormatter = null,
  chipSxResolver = null
}) => {
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [editCharModalOpen, setEditCharModalOpen] = useState(false);
  const [characteristicData, setCharacteristicData] = useState(null);
  const [loadingCharacteristic, setLoadingCharacteristic] = useState(false);
  // Tracks whether any visible chip's value is truncated (showing an ellipsis).
  // Used to surface the "View All" button even when only a few filters exist.
  const [hasTruncatedChip, setHasTruncatedChip] = useState(false);
  // Holds refs to the ellipsizable value spans (Factored Securities / Description)
  // keyed by chipKey so we can measure them after render.
  const valueRefs = React.useRef({});

  const registerValueRef = (chipKey) => (el) => {
    if (el) {
      valueRefs.current[chipKey] = el;
    } else {
      delete valueRefs.current[chipKey];
    }
  };

  const navigate = useNavigate();
  const characteristicId = useBondCharacteristicStore(state => state.characteristicId);
  const bondLadderId = useBondCharacteristicStore(state => state.bondLadderId);
  const bondLadderRungId = useBondCharacteristicStore(state => state.bondLadderRungId);
  const resetBondCharacteristicId = useBondCharacteristicStore(state => state.resetBondCharacteristicId);
  // NEW: state/actions needed to drive viewInventory flow
  const setCurrentCharacteristicID = useBondCharacteristicStore(state => state.setCurrentCharacteristicID);
  const resetBondCharacteristicData = useBondCharacteristicStore(state => state.resetBondCharacteristicData);
  const setBondCharacteristicData = useBondCharacteristicStore(state => state.setBondCharacteristicData);
  const setActualOrderData = useActualOrderStore(state => state.setActualOrderData);

  // Utility functions
  // Resolve a value to date parts, or null when it isn't a date. Strings are calendar
  // dates and are read in UTC, so a "...T00:00:00Z" bound shows the same day the Bond
  // Details panel shows whatever the viewer's timezone; Date objects already carry local
  // parts, so those stay local.
  const getDateParts = (val) => {
    if (val instanceof Date && !isNaN(val)) {
      return { y: val.getFullYear(), m: val.getMonth() + 1, d: val.getDate() };
    }
    if (typeof val !== "string") return null;
    const match = val.match(/^\s*(\d{4})[-\s\/](\d{1,2})[-\s\/](\d{1,2})(?:[T\s].*)?\s*$/);
    const parsed = match
      ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
      : new Date(val);
    if (isNaN(parsed)) return null;
    return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth() + 1, d: parsed.getUTCDate() };
  };

  const pad2 = (n) => String(n).padStart(2, "0");

  // Detect common date patterns and Date objects
  const isDateValue = (v) => {
    if (v instanceof Date && !isNaN(v)) return true;
    if (typeof v !== "string") return false;
    // Matches "yyyy mm dd", "yyyy-mm-dd", "yyyy/mm/dd" (allow single-digit month/day),
    // with an optional trailing time part: without it a timestamped bound skips the
    // date branch in formatRange and gets rendered as a raw ISO string
    return /^\d{4}[-\s\/]\d{1,2}[-\s\/]\d{1,2}([T\s].*)?$/.test(v);
  };

  // Format date-like values as "12Nov25"
  const formatDateShort = (val) => {
    if (val == null || val === "") return "";
    const parts = getDateParts(val);
    if (!parts) return val;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${pad2(parts.d)}${months[parts.m - 1] || ""}${String(parts.y).slice(-2)}`;
  };

  // Format to MM/DD/YYYY for maturity display (matches the Bond Details panel)
  const formatDateToMMDDYYYY = (val) => {
    if (val == null || val === "") return "";
    const parts = getDateParts(val);
    if (!parts) return val;
    return `${pad2(parts.m)}/${pad2(parts.d)}/${parts.y}`;
  };

  const hasRangeValue = (value) =>
    value !== undefined && value !== null && value !== "";

  // Limit numeric values to a maximum of 5 decimal places (no forced trailing zeros).
  // Non-numeric values (ratings, strings, etc.) are returned unchanged.
  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === "" || typeof value === "boolean") {
      return value;
    }
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Number(num.toFixed(5)).toString();
  };

  const shouldShowZeroLowerBound = (key, max) => {
    if (!hasRangeValue(max)) return false;

    const normalizedKey = `${key || ""}`.toLowerCase();
    const nonNegativeRangeKeys = [
      "coupon",
      "maturity",
      "price",
      "duration",
    ];

    return nonNegativeRangeKeys.some((rangeKey) =>
      normalizedKey.includes(rangeKey)
    );
  };

  const formatRange = (min, max, key) => {
    // Detect if this range is related to maturity so we can use MM/DD/YYYY
    const isMaturityKey = typeof key === 'string' && key.toLowerCase().includes('maturity');
    const hasMin = hasRangeValue(min);
    const hasMax = hasRangeValue(max);

    // If either looks like a date, format them using the appropriate formatter
    if (isDateValue(min) || isDateValue(max)) {
      const fmt = isMaturityKey ? formatDateToMMDDYYYY : formatDateShort;
      const a = hasMin ? fmt(min) : "";
      const b = hasMax ? fmt(max) : "";
      if (a && b) return `${a} to ${b}`;
      if (a) return `${a}+`;
      if (b) return `Up to ${b}`;
      return "";
    }
    const minFmt = formatDecimal(min);
    const maxFmt = formatDecimal(max);
    if (hasMin && hasMax) return `${minFmt} to ${maxFmt}`;
    if (hasMin) return `${minFmt}+`;
    if (hasMax && shouldShowZeroLowerBound(key, max)) return `0 to ${maxFmt}`;
    if (hasMax) return `Up to ${maxFmt}`;
    return "";
  };

  const formatBooleanValue = (key, value) => {
    const normalizedKey = typeof key === "string" ? key.split("__")[0] : key;
    const booleanFields = [
      "zeroCoupon",
      "insured",
      "altMinTax",
      "taxable",
      "callable",
      "call",
      "perpetual",
    ];
    if (booleanFields.includes(normalizedKey)) {
      const normalizedValue = `${value}`.trim().toLowerCase();
      return normalizedValue === "1" || normalizedValue === "y" || normalizedValue === "yes"
        ? "Yes"
        : "No";
    }
    if (normalizedKey === "minMaturity") {
      return formatDateToMMDDYYYY(value);
    }
    if (typeof normalizedKey === "string" && normalizedKey.toLowerCase().includes("maturity")) {
      return formatDateToMMDDYYYY(value);
    }
    if (
      (typeof normalizedKey === "string" && normalizedKey.toLowerCase().includes("date")) ||
      isDateValue(value)
    ) {
      return formatDateShort(value);
    }
    if (normalizedKey === "securityCharge" && Array.isArray(value)) {
      return value.join(", ");
    }
    return formatDecimal(value);
  };

  // Add function to format field labels for better display
  const formatFieldLabel = (key) => {
    if (labelFormatter) {
      const override = labelFormatter(key);
      if (override !== undefined && override !== null) return override;
    }
    const baseKey = typeof key === "string" ? key.split("__")[0] : key;
    const labelMap = {
      minimumRating: "Min S&P Rating",
      minimumRatingMoody: "Min Moody's Rating",
      underlyingRating: "Underlying Rating",
      paymentFrequency: "Payment Freq",
      incomeSource: "Income Source",
      securityCharge: "Factored Securities",
      altMinTax: "Alt Min Tax",
      zeroCoupon: "Zero Coupon",
      callable: "Callable",
      call: "Callable",
      insured: "Insured",
      perpetual: "Perpetual",
      taxable: "Taxable",
      callStartDate: "Call Start Date",
      nextCallDate: "Next Call Date",
      issuer: "Issuer",
      description: "Desc",
      textIncludes: "Includes",
      textExcludes: "Excludes",
    };

    if (!baseKey || typeof baseKey !== "string") return baseKey;

    if (labelMap[baseKey]) return labelMap[baseKey];

    const spaced = baseKey
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_\-\.\:]+/g, " ")
      .trim();

    const words = spaced.split(/\s+/).map((w) => {
      if (w.toUpperCase() === w) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    });

    return words.join(" ");
  };

  // Check if we should show "Modify Characteristic" instead of "Edit"
  const shouldShowModifyCharacteristic = characteristicId && 
    characteristicId !== null && 
    characteristicId !== undefined && 
    characteristicId !== '';

  // Check if we can show the edit button based on source
  const canShowEdit = input && (!input.source || 
    (input.source !== "characteristics" && input.source !== "ladder" && !readOnly));

  // Filter entries logic
  const getFilterEntries = () => {
    const entries = Object.entries(input || {})
      .filter(([key]) => key !== "source")
      .filter(([key, value]) => value !== undefined && value !== "");

    // Debug: log the raw entries being processed
    console.log('FilterChips - raw entries:', entries);

    const processedRangeKeys = new Set();
    const finalEntries = [];

    entries.forEach(([key, value]) => {
      // Debug: log each entry as it's processed
      console.log('FilterChips - processing entry:', key, value);

      // First, explicitly handle fields that should NOT be processed as ranges
      if (key === "minimumRating" || key === "underlyingRating" || 
          key === "minimumRatingMoody" ) {
        finalEntries.push([key, value]);
        return;
      }

      // Only process actual range fields (minX/maxX pairs) that are true range fields
      if (key.startsWith("min") || key.startsWith("max")) {
        const baseKey = key.replace(/^(min|max)/, "");
        
        // Additional check: make sure this is actually a range field by checking if both min and max versions might exist
        const hasMinVersion = entries.some(([k]) => k === `min${baseKey}`);
        const hasMaxVersion = entries.some(([k]) => k === `max${baseKey}`);
        
        if (hasMinVersion || hasMaxVersion) {
          if (processedRangeKeys.has(baseKey)) return;
          processedRangeKeys.add(baseKey);
          const minValue = input[`min${baseKey}`];
          const maxValue = input[`max${baseKey}`];
          if (minValue !== undefined || maxValue !== undefined) {
            const deletionKey = minValue !== undefined ? `min${baseKey}` : `max${baseKey}`;
            const displayValue = minValue !== undefined ? minValue : maxValue;
            finalEntries.push([deletionKey, displayValue]);
          }
        } else {
          // This is a field that starts with min/max but isn't actually a range field
          finalEntries.push([key, value]);
        }
      } else {
        finalEntries.push([key, value]);
      }
    });

    // Debug: log the final entries after processing
    console.log('FilterChips - finalEntries:', finalEntries);

    return finalEntries;
  };

  const filterEntries = getFilterEntries();
  // Stable signature of the current filters so the overflow effect only re-runs
  // when the set of chips actually changes (filterEntries is a new array each render).
  const overflowSignature = filterEntries.map(([k, v]) => `${k}:${v}`).join("|");
  // Show "View All" when there are more filters than we can display inline OR when
  // any single visible chip's value is ellipsized (exceeds its max width).
  const showViewAllButton =
    filterEntries.length > maxVisibleChips || hasTruncatedChip;
  const hideInlineClearAll = input?.source === "ma-watchlist";

  // Measure the ellipsizable value spans after render to detect truncation.
  // When filters.length <= maxVisibleChips, slicing to maxVisibleChips keeps every
  // chip mounted, so toggling hasTruncatedChip never removes a chip (no feedback loop).
  React.useLayoutEffect(() => {
    const checkOverflow = () => {
      const truncated = Object.values(valueRefs.current).some(
        (el) => el && el.scrollWidth > el.clientWidth + 1,
      );
      setHasTruncatedChip(truncated);
    };

    checkOverflow();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;
    Object.values(valueRefs.current).forEach((el) => {
      if (el && observer) observer.observe(el);
    });
    window.addEventListener("resize", checkOverflow);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overflowSignature, compact, maxVisibleChips]);

  // New handler for chip deletion that handles the last filter case
  const handleChipDelete = (key) => {
    // If this is the last filter, do a complete clear
    if (filterEntries.length === 1) {
      onClearFilters();
    } else {
      // Otherwise just delete the individual filter
      onDeleteChip(key);
    }
  };

  const handleClearAll = () => {
    setViewAllModalOpen(false);
    onClearFilters();
  };

  const handleEditClick = () => {
    // If a saved filter is active, use the saved filter edit behavior
    if (selectedFilter && savedFilters && onEditSavedFilter) {
      const filter = savedFilters.find(f => f.filterName === selectedFilter);
      if (filter) {
        onEditSavedFilter(filter);
        return;
      }
    }
    
    // Otherwise, use the regular edit behavior
    if (onEditFilter) {
      onEditFilter(input);
    }
  };

  // New helper to truncate long description text for outside display
  const truncateText = (text, maxChars) => {
    if (!text) return "";
    if (text.length <= maxChars) return text;
    return text.slice(0, maxChars).trimEnd() + "...";
  };

  const handleModifyCharacteristic = async () => {
    try {
      setLoadingCharacteristic(true);
      const token = localStorage.getItem("token");
      const data = await getBondCharacteristicById(token, characteristicId);
      if (data) {
        // Set the raw data without processing
        setCharacteristicData(data);
        setEditCharModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching characteristic:", error);
      alert("Failed to load characteristic data");
    } finally {
      setLoadingCharacteristic(false);
    }
  };

  const handleCharacteristicUpdate = (updatedData) => {
    setEditCharModalOpen(false);
    
    // Reload with new filters by routing through the shared view-inventory flow
    if (updatedData) {
      try {
        // Flatten updated characteristic like columnConfig flow
        const flattenedOrder = processBondData(updatedData);
        flattenedOrder.characteristicId = flattenedOrder.characteristicMstId;
        // Editing the characteristic keeps it a characteristic, ladder linkage included.
        // The update response does not always echo the rung ids back, so keep the ones
        // already in context rather than letting handleViewInventory null them out.
        flattenedOrder.bondLadderId = flattenedOrder.bondLadderId ?? bondLadderId ?? null;
        flattenedOrder.bondLadderRungId =
          flattenedOrder.bondLadderRungId ?? bondLadderRungId ?? null;
        // Reset and set store data to ensure consistent state
        resetBondCharacteristicData();
        setBondCharacteristicData(flattenedOrder);
        
        // Go through the same centralized navigation handler
        handleViewInventory(
          1, // activeTab for characteristic
          flattenedOrder,
          setCurrentCharacteristicID,
          setActualOrderData,
          navigate,
          (errMsg) => {
            console.error("Error navigating to inventory:", errMsg);
            alert(errMsg || "Failed to navigate to inventory.");
          },
          () => {} // no-op setLoading
        );
      } catch (e) {
        console.error("Error handling characteristic update navigation:", e);
        alert("Failed to navigate to inventory after update");
      }
    }
  };

  if (!input || Object.keys(input).length === 0) {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={compact ? 0.5 : 1} useFlexGap flexWrap="wrap" sx={{ display: "flex", alignItems: "center" }}>
        {filterEntries
          .slice(0, showViewAllButton ? maxVisibleChips : filterEntries.length)
          .map(([key, value]) => {
            let label = "";
            let chipKey = key;
            
            // First check if this is explicitly NOT a range field
            if (key === "minimumRating" || key === "underlyingRating" || 
                key === "minimumRatingMoody" ) {
              label = `${formatFieldLabel(key)}: ${formatBooleanValue(key, value)}`;
            }
            // Then handle actual range fields
            else if (key.startsWith("min") || key.startsWith("max")) {
              const baseKey = key.replace(/^(min|max)/, "");
              const minValue = input[`min${baseKey}`];
              const maxValue = input[`max${baseKey}`];
              if (key.startsWith("min") || (key.startsWith("max") && minValue === undefined)) {
                // Use formatted base key label instead of lowercase
                label = `${formatFieldLabel(baseKey)}: ${formatRange(minValue, maxValue, baseKey)}`;
                chipKey = `range-${baseKey}`;
              } else {
                return null;
              }
            } 
            // Handle all other fields (including description)
            else {
              label = `${formatFieldLabel(key)}: ${formatBooleanValue(key, value)}`;
            }
            
            const baseLabelKey = typeof key === "string" ? key.split("__")[0] : key;

            // For outside display, if this is the description field, truncate (no tooltip)
            let labelNode = label;
            if (baseLabelKey === "securityCharge") {
              // Keep the "Factored Securities:" prefix fixed and ellipsize only the
              // (potentially multi-select) value when it exceeds the max width.
              const prefix = `${formatFieldLabel(key)}: `;
              const valueText = formatBooleanValue(key, value);
              labelNode = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ flexShrink: 0 }}>{prefix}</span>
                  <span ref={registerValueRef(chipKey)} style={{
                    display: 'inline-block',
                    maxWidth: compact ? 120 : 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {valueText}
                  </span>
                </span>
              );
            } else if (key === "description") {
              const maxChars = compact ? 50 : 100; // adjust truncation length
              const fullText = value || "";
              const truncated = truncateText(fullText, maxChars);
              const prefix = `${formatFieldLabel(key)}: `; // e.g. "Desc: "
              // Render prefix + truncated value and rely on chip CSS to ellipsize
              labelNode = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{prefix}</span>
                  <span ref={registerValueRef(chipKey)} style={{
                    display: 'inline-block',
                    maxWidth: compact ? 160 : 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {truncated}
                  </span>
                </span>
              );
            }

            // Build base sx for chips and customize for description to enforce truncation
            const baseChipSx = {
              borderRadius: "8px",
              fontFamily: "Lato",
              fontWeight: 700,
              fontSize: compact ? "0.75rem" : "14px",
              lineHeight: compact ? "14px" : "16px",
              textTransform: "none",
              backgroundColor: tokens.colors.defaultGridRowColor,
              color: tokens.colors.fontColour,
              border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              "& .MuiChip-deleteIcon": { 
                color: tokens.colors.fontColour, 
                fontSize: compact ? "12px" : "14px" 
              },
              ...(compact && {
                height: '22px',
                '& .MuiChip-label': {
                  padding: '0 6px',
                }
              })
            };

            const descriptionLabelStyles = {
              maxWidth: compact ? 160 : 300,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block'
            };

            // If chipSxResolver returns { valueColor }, use it to color only the value text.
            // Otherwise, merge returned object into chip sx (legacy behavior).
            const resolverResult = chipSxResolver ? chipSxResolver(key, value) : undefined;
            let valueColor;
            let additionalChipSx = {};
            if (resolverResult && typeof resolverResult === "object") {
              if ("valueColor" in resolverResult) {
                valueColor = resolverResult.valueColor;
                additionalChipSx = { ...resolverResult };
                delete additionalChipSx.valueColor;
              } else {
                additionalChipSx = resolverResult;
              }
            }

            // securityCharge uses the standard chip styling: the chip sizes to its
            // content (no fixed maxWidth) and the inner value span's own
            // maxWidth + ellipsis handles truncation, so the chip border always
            // contains the text and a real "…" is shown when it overflows.
            const chipSx = key === "description"
              ? {
                  ...baseChipSx,
                  '& .MuiChip-label': {
                    ...(baseChipSx['& .MuiChip-label'] || {}),
                    ...descriptionLabelStyles
                  }
                }
              : baseChipSx;

            const finalChipSx = additionalChipSx && Object.keys(additionalChipSx).length
              ? { ...chipSx, ...additionalChipSx }
              : chipSx;

            // If a valueColor was provided by resolver, wrap the label so only the value part is colored.
            let renderedLabel = labelNode;
            if (valueColor && typeof labelNode === "string") {
              const sep = ": ";
              const idx = labelNode.indexOf(sep);
              if (idx !== -1) {
                const prefix = labelNode.slice(0, idx + sep.length);
                const val = labelNode.slice(idx + sep.length);
                renderedLabel = (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{prefix}</span>
                    <span style={{ color: valueColor }}>{val}</span>
                  </span>
                );
              } else {
                renderedLabel = <span style={{ color: valueColor }}>{labelNode}</span>;
              }
            } else if (key === "description" && valueColor && React.isValidElement(labelNode)) {
              // description labelNode is built as JSX: apply valueColor to its value span by adjusting inline styles
              // rebuild description node to include color for the truncated span
              const maxChars = compact ? 50 : 100;
              const fullText = value || "";
              const truncated = truncateText(fullText, maxChars);
              const prefix = `${formatFieldLabel(key)}: `;
              renderedLabel = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{prefix}</span>
                  <span ref={registerValueRef(chipKey)} style={{
                    display: 'inline-block',
                    maxWidth: compact ? 160 : 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: valueColor
                  }}>
                    {truncated}
                  </span>
                </span>
              );
            }

            const chipElement = (
               <Chip
                 sx={finalChipSx}
                 key={chipKey}
                 deleteIcon={!readOnly ? <CloseIcon style={{ fontSize: compact ? "12px" : "14px", color: tokens.colors.fontColour }} /> : null}
                 onDelete={!readOnly ? (event) => {
                   event.stopPropagation();
                   handleChipDelete(key);
                 } : undefined}
                 label={renderedLabel}
                 variant="outlined"
               />
             );

            if (label && baseLabelKey === "securityCharge") {
              return chipElement;
            }

            return label ? chipElement : null;
           })}
         {showViewAllButton ? (
           <>
             <Button 
               variant="text" 
               onClick={(event) => {
                 event.stopPropagation();
                 setViewAllModalOpen(true);
               }}
               sx={{
                 ...(compact ? { 
                   fontSize: '13px',  // Increased from 11px to 13px (2px larger)
                   padding: '2px 4px',
                   minWidth: 'auto'
                 } : {}),
                border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                color: tokens.colors.fontColour,
                borderRadius: "8px",
                '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
               }}
             >
               View All
             </Button>
 
             {(canShowEdit && shouldShowModifyCharacteristic) ? (
               <Button 
                 variant="text" 
                 startIcon={loadingCharacteristic ? <CircularProgress size={14} color="inherit" /> : <EditIcon fontSize="small" />}
                 onClick={(event) => {
                   event.stopPropagation();
                   handleModifyCharacteristic();
                 }}
                 disabled={loadingCharacteristic}
                 sx={{
                   ...(compact ? { 
                     fontSize: '13px',
                     padding: '2px 4px',
                     minWidth: 'auto'
                   } : {}),
                  border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                  color: tokens.colors.fontColour,
                  borderRadius: "8px",
                  '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
                 }}
               >
                 {loadingCharacteristic ? "Loading..." : "Edit Characteristic"}
               </Button>
              ) : (
                canShowEdit && onEditFilter && (
                  <Button 
                    variant="text" 
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEditClick();
                    }}
                    sx={{
                      ...(compact ? { 
                        fontSize: '13px',
                        padding: '2px 4px',
                        minWidth: 'auto'
                      } : {}),
                      border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                      color: tokens.colors.fontColour,
                      borderRadius: "8px",
                      '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
                    }}
                  >
                    Edit
                  </Button>
                )
              )}
           </>
         ) : (
           Object.keys(input).some((key) => key !== "source") && !readOnly && (
             <>
               {shouldShowModifyCharacteristic ? (
                 <Button 
                   variant="text" 
                   startIcon={loadingCharacteristic ? <CircularProgress size={14} color="inherit" /> : <EditIcon fontSize="small" />}
                   onClick={(event) => {
                     event.stopPropagation();
                     handleModifyCharacteristic();
                   }}
                   disabled={loadingCharacteristic}
                   sx={{
                     ...(compact ? { 
                       fontSize: '13px',
                       padding: '2px 4px',
                       minWidth: 'auto'
                     } : {}),
                    border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                    color: tokens.colors.fontColour,
                    borderRadius: "8px",
                    '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
                   }}
                 >
                   {loadingCharacteristic ? "Loading..." : "Edit Characteristic"}
                 </Button>
                ) : (
                  canShowEdit && onEditFilter && (
                    <Button 
                      variant="text" 
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditClick();
                      }}
                      sx={{
                        ...(compact ? { 
                          fontSize: '13px',
                          padding: '2px 4px',
                          minWidth: 'auto'
                        } : {}),
                       border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                       color: tokens.colors.fontColour,
                       borderRadius: "8px",
                       '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
                      }}
                    >
                      Edit
                    </Button>
                  )
                )}
               {!hideInlineClearAll ? (
                 <Button 
                   variant="text" 
                   onClick={(event) => {
                     event.stopPropagation();
                     onClearFilters();
                   }}
                   sx={{
                     ...(compact ? { 
                       fontSize: '11px', 
                       padding: '2px 4px',
                       minWidth: 'auto'
                     } : {}),
                    border: `1px solid ${tokens.palette.primaryA25 || tokens.palette.primary}`,
                    color: tokens.colors.fontColour,
                    borderRadius: "8px",
                    '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor }
                   }}
                 >
                   Clear All
                 </Button>
               ) : null}
             </>
           )
         )}
       </Stack>
 
      <ViewAllFiltersModal
        open={viewAllModalOpen}
        onClose={() => setViewAllModalOpen(false)}
        filters={filterEntries}
        input={input}
        onDeleteChip={handleChipDelete}
        onClearFilters={handleClearAll}
        formatRange={formatRange}
        formatBooleanValue={formatBooleanValue}
        readOnly={readOnly}
        formatFieldLabel={formatFieldLabel}
        onEditFilter={canShowEdit && onEditFilter ? () => {
          setViewAllModalOpen(false);
          handleEditClick();
        } : undefined}
        onModifyCharacteristic={shouldShowModifyCharacteristic ? () => {
          setViewAllModalOpen(false);
          handleModifyCharacteristic();
        } : undefined}
        loadingCharacteristic={loadingCharacteristic}
        ladderName={ladderName}
        chipSxResolver={chipSxResolver}
      />

      {/* Edit Characteristic Modal */}
      {editCharModalOpen && characteristicData && (
        <EditBondCharacteristic
          open={editCharModalOpen}
          onClose={() => {
            setEditCharModalOpen(false);
            setCharacteristicData(null);
          }}
          characteristicData={characteristicData}
          onSubmit={handleCharacteristicUpdate}
        />
      )}
    </>
  );
};

export default FilterChips;
